import { uniqBy } from 'lodash';
import dayjs from 'dayjs';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/user/models/User';
import { Conversation } from '@/modules/messenger/_modules/conversation/models/Conversation';
import { Attachment } from '@/modules/messenger/_modules/attachment/models/Attachment';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { humanizeDate, isToday } from '@/modules/core/app/helpers/DateHelper';
import { MessageType } from '@/modules/messenger/_modules/message/constants/MessageType';
import { MessageMention } from '@/modules/messenger/_modules/message/models/MessageMention';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { RegexPattern } from '@/modules/core/app/constants/RegexPattern';
import { MessageDetail } from '@/modules/messenger/_modules/message/models/MessageDetail';
import { MessageClaraDraft } from '@/modules/messenger/_modules/message/models/MessageClaraDraft';
import { getTextFromHTML } from '@/modules/core/app/helpers/StringHelper';
import { ReadReceipt } from '@/modules/readreceipt/models/ReadReceipt';
import { MessageSubType } from '@/modules/messenger/_modules/message/constants/MessageSubType';
import Lang from '@/modules/core/app/services/Lang';
import { MessageTemplate } from '@/modules/messenger/_modules/message/models/MessageTemplate';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';

export class Message extends BaseEntityModel {
  id?: number;
  userId?: number;
  conversationId?: number;
  talkjsId?: string;
  text?: string;
  type?: string;
  subType?: string;
  isDraft?: boolean;
  messageTemplateId?: number;
  key: string;
  user?: Relationship<User>;
  conversation?: Relationship<Conversation>;
  messageMentions?: Relationship<MessageMention[]>;
  messageDetail?: Relationship<MessageDetail>;
  claraDraft?: Relationship<MessageClaraDraft> | null;
  readReceipts?: Relationship<ReadReceipt[]>;
  attachments?: Relationship<Attachment[]>;
  messageTemplate?: Relationship<MessageTemplate> | null;

  constructor(model: Partial<Message> = {}) {
    super(model);

    this.id = model.id;
    this.userId = model.userId;
    this.conversationId = model.conversationId;
    this.talkjsId = model.talkjsId;
    this.text = model.text;
    this.type = model.type;
    this.subType = model.subType;
    this.isDraft = model.isDraft;
    this.messageTemplateId = model.messageTemplateId;
    this.key = uuidv4();

    // @ts-ignore
    this.user = User.mapModelOrNull(model.user);
    // @ts-ignore
    this.conversation = Conversation.mapModelOrNull(model.conversation);
    // @ts-ignore
    this.attachments = Attachment.mapModelsOrNull(model.attachments);
    // @ts-ignore
    this.messageMentions = MessageMention.mapModelsOrNull(model.messageMentions);
    // @ts-ignore
    this.messageDetail = MessageDetail.mapModelOrNull(model.messageDetail);
    this.claraDraft = MessageClaraDraft.mapModelOrNull(model.claraDraft);
    // @ts-ignore
    this.readReceipts = ReadReceipt.mapModelsOrNull(model.readReceipts);
    this.messageTemplate = MessageTemplate.mapModelOrNull(model.messageTemplate);
  }

  getModelName(): ModelType {
    return ModelType.MESSAGE;
  }

  addUserMentions(users: User[], mentionableUsers: User[]): void {
    this.messageMentions = uniqBy(this.getUsersMentionedInText(users, mentionableUsers), 'id').map(
      (user: User) =>
        MessageMention.mapModel({
          modelType: ModelType.USER,
          modelId: user.id,
          user: { ...user, teamUsers: null } as User
        })
    );
  }

  mergeUserMentions(users: User[], mentionableUsers: User[]): void {
    if (!this.messageMentions?.length) {
      return this.addUserMentions(users, mentionableUsers);
    }

    const userMentionedInText: User[] = this.getUsersMentionedInText(users, mentionableUsers);
    this.removeNotMentionedUsers(userMentionedInText);

    const existingMentionedIds: number[] | undefined =
      this.messageMentions?.map((mention: MessageMention) => mention.modelId as number) ?? [];

    userMentionedInText
      .filter((user: User) => !existingMentionedIds.includes(user.id as number))
      .forEach((user: User): void => {
        this.messageMentions?.push(
          MessageMention.mapModel({
            modelType: ModelType.USER,
            modelId: user.id,
            user: { ...user, teamUsers: null } as User
          })
        );
      });
  }

  private removeNotMentionedUsers(userMentionedInText: User[]): void {
    this.messageMentions = this.messageMentions?.filter((mention: MessageMention) =>
      userMentionedInText.some((user: User): boolean => user.id === mention.modelId)
    );
  }

  private getUsersMentionedInText(users: User[], mentionableUsers: User[]): User[] {
    return uniqBy(users, 'id').filter((user: User) =>
      this.text?.includes(
        `@${
          mentionableUsers.find((mentionableUser: User): boolean => mentionableUser.id === user.id)
            ?.name
        }`
      )
    );
  }

  hasMentionedUser(userId: number): boolean {
    return !!this.messageMentions?.some(
      (messageMention: MessageMention): boolean => messageMention.modelId === userId
    );
  }

  get displayCreatedAt(): string {
    return isToday(this.createdAt)
      ? this.timeAgoCreatedAt
      : dayjs(this.createdAt).format(DateFormat.MONTH_DAY_WITH_ORDINAL_TWELVE_HOUR);
  }

  get displayUpdatedAt(): string {
    return isToday(this.updatedAt)
      ? this.timeAgoUpdatedAt
      : dayjs(this.updatedAt).format(DateFormat.MONTH_DAY_WITH_ORDINAL_TWELVE_HOUR);
  }

  get displayTextHtml(): string {
    let text: string = this.text ?? '';
    text = this.parseUrls(text);
    text = this.parseTalkJsText(text);

    return text;
  }

  getSystemMessage(users: User[]): string {
    if (!this.isSystemMessage || !this.subType) {
      return this.displayTextHtml;
    }

    return (
      MessageSubType.getByKey(this.subType)?.getValue(
        this,
        users.find((user: User) => user?.id === this.systemMessageUserId)
      ) ?? ''
    );
  }

  private get systemMessageUserId(): number {
    switch (this.subType) {
      case MessageSubType.ADD_USER.key:
      case MessageSubType.REMOVE_USER.key:
        return parseInt(this.text?.split('|').pop() as string);
      case MessageSubType.ARCHIVE.key:
      case MessageSubType.UNARCHIVE.key:
      default:
        return this.userId as number;
    }
  }

  private parseUrls(text: string): string {
    if (RegexPattern.HTML.test(text)) {
      return text;
    }

    // Transforming only the older comment data
    return text?.replace(
      // temporary change (to not modify URLs of type - <URL|Name>
      new RegExp(`^(?!<)${RegexPattern.URL.source}`, 'g'),
      (url: string) => `<a href="${url}" target="_blank">${url}</a>`
    );
  }

  /**
   * Temporary change until we know everything is stable.
   */
  private parseTalkJsText(text: string): string {
    if (!this.talkjsId) {
      return text;
    }

    text = this.parseBold(text);
    text = this.parseItalic(text);
    text = this.parseStrikeThrough(text);
    text = this.parseTalkJsUrls(text);

    return text;
  }

  private parseTalkJsUrls(text: string): string {
    return text?.replace(
      new RegExp(`<${RegexPattern.URL.source}\\|(.*?)?>`, 'g'),
      (_: string, url: string, label: string) => `<a href="${url}" target="_blank">${label}</a>`
    );
  }

  private parseBold(text: string): string {
    return text?.replace(
      new RegExp('\\*([^ ].*?)\\*', 'g'),
      (_: string, value: string) => `<strong>${value}</strong>`
    );
  }

  private parseItalic(text: string): string {
    return text?.replace(
      new RegExp('_([^ ].*?)_', 'g'),
      (_: string, value: string) => `<em>${value}</em>`
    );
  }

  private parseStrikeThrough(text: string): string {
    return text?.replace(
      new RegExp('~([^ ].*?)~', 'g'),
      (_: string, value: string) => `<s>${value}</s>`
    );
  }

  get isBotMessage(): boolean {
    return this.type === MessageType.BOT;
  }

  get isDeletedUserMessage(): boolean {
    return this.type === MessageType.DELETED_USER;
  }

  get isSystemMessage(): boolean {
    return this.type === MessageType.SYSTEM;
  }

  get hasClaraDraft(): boolean {
    return !!this.isDraft && !!this.claraDraft;
  }

  get sentiment(): string {
    return this.messageDetail ? this.messageDetail.sentimentEnum?.value ?? '' : '';
  }

  get isPositive(): boolean {
    return this.messageDetail?.isPositive ?? false;
  }

  get isMixed(): boolean {
    return this.messageDetail?.isMixed ?? false;
  }

  get isNegative(): boolean {
    return this.messageDetail?.isNegative ?? false;
  }

  get textContent(): string {
    return getTextFromHTML(this.text);
  }

  get humanizedUpdatedAt(): string {
    return humanizeDate(this.updatedAt);
  }

  get humanizedWithOnUpdatedAt(): string {
    return humanizeDate(this.updatedAt, true, Lang.t('core.on-spaced'));
  }
}
